import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

interface LicenseRequest {
  id: string;
  recipient_email: string;
  recipient_first_name: string | null;
  recipient_last_name: string | null;
  status: 'pending' | 'completed' | 'expired' | 'cancelled';
  requested_at: string;
  completed_at: string | null;
  expires_at: string;
  requester_first_name: string;
  requester_last_name: string;
  staff_first_name: string | null;
  staff_last_name: string | null;
}

const DVLALicenseRequestsTable: React.FC = () => {
  const [requests, setRequests] = useState<LicenseRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/dvla-license-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data || []);
    } catch (err: any) {
      console.error('Error fetching license requests:', err);
      setError(err.response?.data?.error || 'Failed to fetch license requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleCancelRequest = async () => {
    if (!selectedRequestId) return;

    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/dvla-license-requests/${selectedRequestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCancelDialogOpen(false);
      setSelectedRequestId(null);
      fetchRequests();
    } catch (err: any) {
      console.error('Error cancelling request:', err);
      setError(err.response?.data?.message || 'Failed to cancel request');
    } finally {
      setCancelling(false);
    }
  };

  const handleResendRequest = async (requestId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/dvla-license-requests/${requestId}/resend`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('Request email resent successfully');
    } catch (err: any) {
      console.error('Error resending request:', err);
      alert(err.response?.data?.message || 'Failed to resend request');
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip icon={<HourglassEmptyIcon />} label="Pending" color="warning" size="small" />;
      case 'completed':
        return <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size="small" />;
      case 'expired':
        return <Chip icon={<ErrorIcon />} label="Expired" color="error" size="small" />;
      case 'cancelled':
        return <Chip label="Cancelled" color="default" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const hoursUntilExpiry = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilExpiry < 24 && hoursUntilExpiry > 0;
  };

  if (loading && requests.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">License Check Requests</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchRequests}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {requests.length === 0 ? (
        <Alert severity="info">
          No license check requests found. Click "Request License Check" to send a request.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Recipient</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Requested At</TableCell>
                <TableCell>Expires At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => {
                const recipientName = request.recipient_first_name && request.recipient_last_name
                  ? `${request.recipient_first_name} ${request.recipient_last_name}`
                  : request.recipient_first_name || request.recipient_last_name || '-';
                
                const requesterName = `${request.requester_first_name} ${request.requester_last_name}`.trim();

                return (
                  <TableRow key={request.id}>
                    <TableCell>{recipientName}</TableCell>
                    <TableCell>{request.recipient_email}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusChip(request.status)}
                        {request.status === 'pending' && isExpiringSoon(request.expires_at) && (
                          <Tooltip title="Expires within 24 hours">
                            <ErrorIcon color="warning" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{requesterName}</TableCell>
                    <TableCell>{formatDate(request.requested_at)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={
                          request.status === 'pending' && isExpiringSoon(request.expires_at)
                            ? 'warning.main'
                            : 'text.primary'
                        }
                      >
                        {formatDate(request.expires_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {request.status === 'pending' && (
                          <>
                            <Tooltip title="Resend Email">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleResendRequest(request.id)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel Request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedRequestId(request.id);
                                  setCancelDialogOpen(true);
                                }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {request.status === 'completed' && request.staff_first_name && (
                          <Typography variant="caption" color="success.main">
                            ✓ Added: {request.staff_first_name} {request.staff_last_name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => !cancelling && setCancelDialogOpen(false)}>
        <DialogTitle>Cancel License Check Request</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this license check request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            No, Keep It
          </Button>
          <Button
            onClick={handleCancelRequest}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={20} /> : null}
          >
            {cancelling ? 'Cancelling...' : 'Yes, Cancel Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DVLALicenseRequestsTable;



